/**
 * Serialized cart-write queue, debounced attributes, coalesced cart events.
 * Load synchronously in <head> before theme.js (no defer).
 * Mutations only — reads pass through native fetch untouched (body never consumed here).
 */
(function () {
  if (window.__SC_CART_QUEUE_INIT__) return;
  window.__SC_CART_QUEUE_INIT__ = true;

  if (typeof window.SC_DEBUG === 'undefined') window.SC_DEBUG = false;

  var nativeFetch = window.fetch.bind(window);
  window.__SC_NATIVE_FETCH__ = nativeFetch;

  var mutationQueue = [];
  var mutationRunning = false;
  var reconcileInFlight = false;

  var DEBOUNCED_ATTR_KEYS = ['bird_name', 'species_breed', 'species_breed2', '_species_source'];
  var pendingAttributes = Object.create(null);
  var attrDebounceTimer = null;
  var pendingNote = null;
  var noteDebounceTimer = null;
  var ATTR_DEBOUNCE_MS = 300;

  var buildCoalesceTimer = null;
  var updatedCoalesceTimer = null;
  var pendingUpdatedCart = null;
  var CART_EVENT_COALESCE_MS = 50;

  var _cartBusyRenderPending = 0;
  var _cartBusyClearTimer = null;
  var _cartBusyWatchdogTimer = null;
  var CART_BUSY_CLEAR_DEBOUNCE_MS = 80;
  var CART_BUSY_WATCHDOG_MS = 12000;

  function isCartWorkActive() {
    return mutationRunning ||
      mutationQueue.length > 0 ||
      reconcileInFlight ||
      _idleReconcileLock ||
      _cartBusyRenderPending > 0;
  }

  function setCartDrawerBusy(isBusy) {
    var drawer = typeof document !== 'undefined' ? document.getElementById('CartDrawer') : null;
    if (!drawer) return;
    if (isBusy) {
      drawer.classList.add('is-cart-busy');
      drawer.setAttribute('aria-busy', 'true');
    } else {
      drawer.classList.remove('is-cart-busy');
      drawer.setAttribute('aria-busy', 'false');
    }
  }

  function forceCartDrawerBusyClear() {
    _cartBusyRenderPending = 0;
    clearTimeout(_cartBusyWatchdogTimer);
    clearTimeout(_cartBusyClearTimer);
    setCartDrawerBusy(false);
  }

  function scheduleCartDrawerBusyCheck() {
    clearTimeout(_cartBusyClearTimer);
    _cartBusyClearTimer = setTimeout(function () {
      if (!isCartWorkActive()) {
        forceCartDrawerBusyClear();
      }
    }, CART_BUSY_CLEAR_DEBOUNCE_MS);
  }

  function syncCartDrawerBusy() {
    clearTimeout(_cartBusyWatchdogTimer);
    if (isCartWorkActive()) {
      setCartDrawerBusy(true);
      _cartBusyWatchdogTimer = setTimeout(forceCartDrawerBusyClear, CART_BUSY_WATCHDOG_MS);
    } else {
      scheduleCartDrawerBusyCheck();
    }
  }

  function markCartRenderPending() {
    _cartBusyRenderPending += 1;
    syncCartDrawerBusy();
  }

  function markCartRenderDone() {
    if (_cartBusyRenderPending > 0) _cartBusyRenderPending -= 1;
    scheduleCartDrawerBusyCheck();
  }

  function bindCartBusyProductsObserver() {
    var attach = function () {
      var products = document.querySelector('#CartDrawer [data-products]');
      if (!products || products.__scBusyObs) return;
      products.__scBusyObs = true;
      new MutationObserver(function () {
        if (_cartBusyRenderPending > 0) markCartRenderDone();
      }).observe(products, { childList: true, subtree: true });
    };
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', attach);
    } else {
      attach();
    }
  }

  bindCartBusyProductsObserver();

  document.addEventListener('cart:build', function () {
    markCartRenderPending();
  }, true);

  function sleep(ms) {
    return new Promise(function (resolve) {
      setTimeout(resolve, ms);
    });
  }

  function normalizeUrl(input) {
    var url = typeof input === 'string' ? input : (input && input.url) || '';
    if (url.indexOf('http') === 0) {
      try {
        var parsed = new URL(url, window.location.origin);
        url = parsed.pathname + parsed.search;
      } catch (e) {}
    }
    return url;
  }

  function requestMethod(input, init) {
    if (init && init.method) return String(init.method).toUpperCase();
    if (input && input.method) return String(input.method).toUpperCase();
    return 'GET';
  }

  function isCartMutationUrl(url) {
    return /\/cart\/(add|change|update)\.js/.test(url);
  }

  function cacheCartJson(cart, at) {
    if (cart && cart.items) {
      window.__SC_LAST_CART__ = cart;
      window.__SC_LAST_CART_AT__ = at != null ? at : Date.now();
    }
    return cart;
  }

  function writeThroughCartCache(response, url) {
    if (!response || !response.ok || !isCartMutationUrl(url)) {
      return Promise.resolve();
    }
    return response.clone().json().then(function (cart) {
      if (window.SC_isValidCart(cart)) cacheCartJson(cart, Date.now());
    }).catch(function () {});
  }

  function syntheticJsonResponse(cart, status) {
    return new Response(JSON.stringify(cart), {
      status: status || 200,
      headers: { 'Content-Type': 'application/json' }
    });
  }

  function parseCartFromResponse(response) {
    if (!response || !response.ok) return Promise.resolve(null);
    return response.clone().json().then(function (cart) {
      return window.SC_isValidCart(cart) ? cart : null;
    }).catch(function () { return null; });
  }

  var POST_MUTATION_CART_RETRY_DELAYS_MS = [80, 100, 120];
  var POST_MUTATION_CART_MAX_ATTEMPTS = 3;

  function isCartSettled(cart) {
    return window.SC_isValidCart(cart) && cart.item_count > 0;
  }

  function fetchCartJsonFromServer() {
    var cartUrl = (window.theme && window.theme.routes && window.theme.routes.cart) || '/cart.js';
    var sep = cartUrl.indexOf('?') === -1 ? '?' : '&';
    return nativeFetch(cartUrl + sep + 't=' + Date.now(), {
      credentials: 'same-origin',
      method: 'GET'
    }).then(function (res) { return res.json(); });
  }

  function refreshCartUntilSettled() {
    var attempt = 0;

    function step() {
      return fetchCartJsonFromServer().then(function (cart) {
        if (isCartSettled(cart)) {
          cacheCartJson(cart, Date.now());
          return cart;
        }
        if (attempt >= POST_MUTATION_CART_MAX_ATTEMPTS - 1) {
          if (window.SC_isValidCart(cart)) cacheCartJson(cart, Date.now());
          return cart;
        }
        attempt += 1;
        var delay = POST_MUTATION_CART_RETRY_DELAYS_MS[
          Math.min(attempt - 1, POST_MUTATION_CART_RETRY_DELAYS_MS.length - 1)
        ];
        return sleep(delay).then(step);
      });
    }

    return step();
  }

  function resolveCartAfterMutation(response, url) {
    return parseCartFromResponse(response).then(function (cart) {
      if (isCartSettled(cart)) return cart;
      if (!response || !response.ok || !isCartMutationUrl(url)) return null;
      return refreshCartUntilSettled();
    });
  }

  window.SC_ensureFullCart = function (cart) {
    if (isCartSettled(cart)) return Promise.resolve(cart);
    return refreshCartUntilSettled();
  };

  function getReconcileDebug(cart) {
    var merch = window.SC_merchandiseSubtotalCents ? window.SC_merchandiseSubtotalCents(cart) : null;
    var thrCents = 14900;
    var bar = typeof document !== 'undefined' ? document.querySelector('[data-sc-reward-bar]') : null;
    if (bar) thrCents = parseInt(bar.getAttribute('data-gift-threshold-cents'), 10) || thrCents;
    var desiredPart = window.SC_desiredThresholdGiftStatePart ? window.SC_desiredThresholdGiftStatePart(cart) : 't:?';
    var currentPart = window.SC_thresholdGiftStatePart ? window.SC_thresholdGiftStatePart(cart) : 't:?';
    var heroDesired = window.SC_desiredHeroGiftStatePart ? window.SC_desiredHeroGiftStatePart(cart) : 'h:?';
    var heroCurrent = window.SC_heroGiftStatePart ? window.SC_heroGiftStatePart(cart) : 'h:?';
    return {
      merchCents: merch,
      thrCents: thrCents,
      desiredGift: desiredPart.indexOf('t:1') === 0,
      currentGift: currentPart.indexOf('t:1') === 0,
      heroDesired: heroDesired,
      heroCurrent: heroCurrent
    };
  }

  function scLogReconcile(source, cart, action, extra) {
    if (!window.SC_DEBUG) return;
    var dbg = cart ? getReconcileDebug(cart) : { merchCents: null, thrCents: 14900, desiredGift: null, currentGift: null, heroDesired: 'h:?', heroCurrent: 'h:?' };
    var tokenOld = extra && extra.tokenOld != null ? extra.tokenOld : _lastReconciledStateToken;
    var tokenNew = extra && extra.tokenNew != null ? extra.tokenNew : (cart ? giftReconcileStateToken(cart) : '');
    console.log(
      '[sc-reconcile] source=' + source +
      ' queueIdle=' + (!mutationRunning && !_idleReconcileLock) +
      ' merchCents=' + dbg.merchCents +
      ' thrCents=' + dbg.thrCents +
      ' desiredGift=' + dbg.desiredGift +
      ' currentGift=' + dbg.currentGift +
      ' heroDesired=' + dbg.heroDesired +
      ' heroCurrent=' + dbg.heroCurrent +
      ' action=' + action +
      ' tokenOld=' + tokenOld +
      ' tokenNew=' + tokenNew
    );
  }

  function runGiftReconcilePipeline(cart) {
    reconcileInFlight = true;
    syncCartDrawerBusy();
    var chain = Promise.resolve(cart);
    if (window.SC_reconcileHeroGiftsForCart) {
      chain = chain.then(function (c) {
        return window.SC_reconcileHeroGiftsForCart(c);
      });
    }
    if (window.SC_reconcileThresholdGiftForCart) {
      chain = chain.then(function (c) {
        return window.SC_reconcileThresholdGiftForCart(c);
      });
    }
    return chain.finally(function () {
      reconcileInFlight = false;
      scheduleCartDrawerBusyCheck();
      drainMutationQueue();
    });
  }

  function giftReconcileStateToken(cart) {
    if (!window.SC_isValidCart(cart)) return '';
    var merch = window.SC_merchandiseSubtotalCents ? window.SC_merchandiseSubtotalCents(cart) : 0;
    var thresh = window.SC_thresholdGiftStatePart ? window.SC_thresholdGiftStatePart(cart) : 't:?';
    var hero = window.SC_heroGiftStatePart ? window.SC_heroGiftStatePart(cart) : 'h:?';
    return 'm:' + merch + '|' + thresh + '|' + hero;
  }

  function desiredGiftReconcileStateToken(cart) {
    if (!window.SC_isValidCart(cart)) return '';
    var merch = window.SC_merchandiseSubtotalCents ? window.SC_merchandiseSubtotalCents(cart) : 0;
    var thresh = window.SC_desiredThresholdGiftStatePart
      ? window.SC_desiredThresholdGiftStatePart(cart)
      : (window.SC_thresholdGiftStatePart ? window.SC_thresholdGiftStatePart(cart) : 't:?');
    var hero = window.SC_desiredHeroGiftStatePart
      ? window.SC_desiredHeroGiftStatePart(cart)
      : (window.SC_heroGiftStatePart ? window.SC_heroGiftStatePart(cart) : 'h:?');
    return 'm:' + merch + '|' + thresh + '|' + hero;
  }

  function needsGiftReconcile(cart) {
    return giftReconcileStateToken(cart) !== desiredGiftReconcileStateToken(cart);
  }

  var _idleReconcileLock = false;
  var _idleReconcilePending = false;
  var _lastReconciledStateToken = '';

  function shouldForceFreshCart(source) {
    if (source === 'init') return true;
    if (window.SC_getLastCart) {
      var settled = window.SC_getLastCart(10000);
      if (window.SC_isValidCart(settled) && !needsGiftReconcile(settled) &&
          _lastReconciledStateToken === giftReconcileStateToken(settled)) {
        return false;
      }
    }
    return source === 'drawer-open' || source === 'product-added' || source === 'pending';
  }

  function getCartForIdleReconcile(source, cartHint) {
    if (cartHint && isCartSettled(cartHint)) {
      return Promise.resolve(cartHint);
    }
    if (source !== 'init' && source !== 'drawer-open' && source !== 'product-added' && window.SC_getLastCart) {
      var cached = window.SC_getLastCart(12000);
      if (isCartSettled(cached) && !needsGiftReconcile(cached)) {
        _lastReconciledStateToken = giftReconcileStateToken(cached);
        return Promise.resolve(cached);
      }
    }
    if (source === 'drawer-open' || source === 'product-added') {
      return refreshCartUntilSettled();
    }
    if (window.SC_getSharedCart) {
      return window.SC_getSharedCart({ forceFresh: shouldForceFreshCart(source) });
    }
    return refreshCartUntilSettled();
  }

  function waitForMutationIdle() {
    return new Promise(function (resolve) {
      (function tick() {
        if (!mutationRunning && !_idleReconcileLock && !reconcileInFlight) {
          resolve();
          return;
        }
        setTimeout(tick, 40);
      })();
    });
  }

  function reconcileGiftsIdle(source, cartHint) {
    if (_idleReconcileLock) {
      _idleReconcilePending = true;
      scLogReconcile(source, cartHint || null, 'skipped:busy');
      return Promise.resolve(null);
    }

    return waitForMutationIdle().then(function () {
      _idleReconcileLock = true;
      syncCartDrawerBusy();

      var cartPromise = getCartForIdleReconcile(source, cartHint);

      return cartPromise.then(function (cart) {
        if (!window.SC_isValidCart(cart) || !cart.item_count) {
          scLogReconcile(source, cart, 'skipped:noCart');
          return null;
        }

        if (!window.SC_reconcileHeroGiftsForCart && !window.SC_reconcileThresholdGiftForCart) {
          scLogReconcile(source, cart, 'skipped:noModules');
          return null;
        }

        var stateBefore = giftReconcileStateToken(cart);
        var desiredToken = desiredGiftReconcileStateToken(cart);

        if (!needsGiftReconcile(cart)) {
          _lastReconciledStateToken = stateBefore;
          scLogReconcile(source, cart, 'noop', { tokenOld: _lastReconciledStateToken, tokenNew: stateBefore });
          return null;
        }

        if (_lastReconciledStateToken === desiredToken) {
          var settledCart = window.SC_getLastCart ? window.SC_getLastCart(15000) : null;
          if (window.SC_isValidCart(settledCart) && giftReconcileStateToken(settledCart) === _lastReconciledStateToken) {
            scLogReconcile(source, settledCart, 'noop:staleRead', {
              tokenOld: stateBefore,
              tokenNew: _lastReconciledStateToken
            });
            cacheCartJson(settledCart);
            return null;
          }
        }

        var action = window.SC_thresholdGiftActionForCart
          ? window.SC_thresholdGiftActionForCart(cart)
          : 'reconcile';
        scLogReconcile(source, cart, action, { tokenOld: _lastReconciledStateToken, tokenNew: stateBefore });

        return runGiftReconcilePipeline(cart).then(function (finalCart) {
          if (!window.SC_isValidCart(finalCart)) return null;
          cacheCartJson(finalCart, Date.now());
          var tokenAfter = giftReconcileStateToken(finalCart);
          if (!needsGiftReconcile(finalCart)) {
            _lastReconciledStateToken = tokenAfter;
            if (tokenAfter === stateBefore) {
              scLogReconcile(source, finalCart, 'noop:afterPipeline', { tokenOld: stateBefore, tokenNew: tokenAfter });
              return null;
            }
            scLogReconcile(source, finalCart, 'mutated', { tokenOld: stateBefore, tokenNew: tokenAfter });
            return finalCart;
          }
          scLogReconcile(source, finalCart, 'incomplete:afterPipeline', { tokenOld: stateBefore, tokenNew: tokenAfter });
          return refreshCartFromServer().then(function (fresh) {
            if (!window.SC_isValidCart(fresh)) return finalCart;
            return runGiftReconcilePipeline(fresh).then(function (retryCart) {
              var settled = window.SC_isValidCart(retryCart) ? retryCart : fresh;
              cacheCartJson(settled, Date.now());
              if (!needsGiftReconcile(settled)) {
                _lastReconciledStateToken = giftReconcileStateToken(settled);
                scLogReconcile(source, settled, 'mutated', {
                  tokenOld: stateBefore,
                  tokenNew: _lastReconciledStateToken
                });
                return settled;
              }
              return settled;
            });
          });
        });
      }).then(function (changedCart) {
        if (changedCart && window.SC_emitCartRefresh) {
          window.SC_emitCartRefresh(changedCart);
        }
        return changedCart;
      }).catch(function (err) {
        console.warn('[SC Cart Queue] idle gift reconcile', source, err);
        return null;
      }).finally(function () {
        _idleReconcileLock = false;
        scheduleCartDrawerBusyCheck();
        if (_idleReconcilePending) {
          _idleReconcilePending = false;
          if (window.SC_getLastCart) {
            var pendingCart = window.SC_getLastCart(15000);
            if (window.SC_isValidCart(pendingCart) && !needsGiftReconcile(pendingCart)) {
              _lastReconciledStateToken = giftReconcileStateToken(pendingCart);
              return;
            }
          }
          reconcileGiftsIdle('pending');
        }
      });
    });
  }

  window.SC_reconcileGiftsIdle = reconcileGiftsIdle;

  function scheduleIdleReconcileOnInit() {
    var attempts = 0;
    function tryInit() {
      attempts += 1;
      if (window.SC_reconcileHeroGiftsForCart || window.SC_reconcileThresholdGiftForCart || attempts > 80) {
        reconcileGiftsIdle('init');
        return;
      }
      setTimeout(tryInit, 50);
    }
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', function () {
        setTimeout(tryInit, 80);
      });
    } else {
      setTimeout(tryInit, 80);
    }
  }

  scheduleIdleReconcileOnInit();

  document.addEventListener('cart:open', function () {
    reconcileGiftsIdle('drawer-open');
  });

  document.addEventListener('ajaxProduct:added', function (evt) {
    if (window.__SC_LAST_CART_AT__ && Date.now() - window.__SC_LAST_CART_AT__ < 8000) {
      var recent = window.__SC_LAST_CART__;
      if (isCartSettled(recent) && !needsGiftReconcile(recent)) {
        return;
      }
    }
    var rawCart = evt && evt.detail && evt.detail.cart;
    var ensure = window.SC_ensureFullCart
      ? window.SC_ensureFullCart(rawCart)
      : Promise.resolve(rawCart);
    ensure.then(function (cart) {
      setTimeout(function () {
        reconcileGiftsIdle('product-added', cart);
      }, 80);
    });
  });

  function refreshCartFromServer() {
    return fetchCartJsonFromServer().then(cacheCartJson);
  }

  function executeMutation(url, init, attempt) {
    var maxAttempts = 3;
    var backoffs = [150, 300, 300];
    attempt = attempt || 0;

    return nativeFetch(url, init).then(function (response) {
      var status = response.status;

      if ((status === 400 || status === 404 || status === 422) && /\/cart\/change\.js/.test(normalizeUrl(url))) {
        return response;
      }

      if (status === 409 && attempt < maxAttempts - 1) {
        return refreshCartFromServer()
          .catch(function () {})
          .then(function () { return sleep(backoffs[attempt]); })
          .then(function () { return executeMutation(url, init, attempt + 1); });
      }

      if (status === 429 && attempt < maxAttempts - 1) {
        return sleep(backoffs[attempt]).then(function () {
          return executeMutation(url, init, attempt + 1);
        });
      }

      if ((status === 409 || status === 429) && attempt >= maxAttempts - 1) {
        console.warn('[SC Cart Queue] mutation failed after retries', status, url);
      }

      writeThroughCartCache(response, normalizeUrl(url));
      return response;
    }).catch(function (err) {
      if (attempt < maxAttempts - 1) {
        return sleep(backoffs[attempt]).then(function () {
          return executeMutation(url, init, attempt + 1);
        });
      }
      console.warn('[SC Cart Queue] mutation error', err);
      return new Response(
        JSON.stringify({ status: 409, message: 'Cart update failed. Please try again.' }),
        {
          status: 409,
          headers: { 'Content-Type': 'application/json' }
        }
      );
    });
  }

  function drainMutationQueue() {
    if (mutationRunning || !mutationQueue.length) return;
    if (reconcileInFlight && mutationQueue[0].origin !== 'system') return;

    mutationRunning = true;
    var job = mutationQueue.shift();
    var jobUrl = normalizeUrl(job.url);

    executeMutation(job.url, job.init, 0)
      .then(function (response) {
        if (job.origin !== 'system' && response && response.ok && isCartMutationUrl(jobUrl)) {
          return resolveCartAfterMutation(response, jobUrl).then(function (cart) {
            if (!isCartSettled(cart)) {
              scLogReconcile('user-mutation', cart, 'skipped:noCartAfterMutation');
              return response;
            }
            var action = window.SC_thresholdGiftActionForCart
              ? window.SC_thresholdGiftActionForCart(cart)
              : 'reconcile';
            scLogReconcile('user-mutation', cart, action, {
              tokenOld: _lastReconciledStateToken,
              tokenNew: giftReconcileStateToken(cart)
            });
            mutationRunning = false;
            return runGiftReconcilePipeline(cart).then(function (finalCart) {
              if (!window.SC_isValidCart(finalCart)) return response;
              cacheCartJson(finalCart, Date.now());
              if (!needsGiftReconcile(finalCart)) {
                _lastReconciledStateToken = giftReconcileStateToken(finalCart);
              }
              scLogReconcile('user-mutation', finalCart, 'done', {
                tokenOld: giftReconcileStateToken(cart),
                tokenNew: giftReconcileStateToken(finalCart)
              });
              return syntheticJsonResponse(finalCart, response.status);
            });
          });
        }
        return response;
      })
      .then(function (response) {
        job.resolve(response);
      })
      .catch(function (err) {
        job.reject(err);
      })
      .finally(function () {
        mutationRunning = false;
        scheduleCartDrawerBusyCheck();
        drainMutationQueue();
      });
  }

  function enqueueMutation(url, init, origin) {
    return new Promise(function (resolve, reject) {
      mutationQueue.push({
        url: url,
        init: init,
        origin: origin || 'user',
        resolve: resolve,
        reject: reject
      });
      syncCartDrawerBusy();
      drainMutationQueue();
    });
  }

  function attributeToString(attribute) {
    if (typeof attribute !== 'string') {
      attribute += '';
      if (attribute === 'undefined') attribute = '';
    }
    return attribute.trim();
  }

  function flushDebouncedAttributes() {
    attrDebounceTimer = null;
    var keys = Object.keys(pendingAttributes);
    if (!keys.length) return Promise.resolve();

    var attrs = {};
    keys.forEach(function (key) {
      attrs[key] = attributeToString(pendingAttributes[key]);
    });
    pendingAttributes = Object.create(null);

    return window.SC_cartFetch('/cart/update.js', {
      method: 'POST',
      body: JSON.stringify({ attributes: attrs }),
      credentials: 'same-origin',
      headers: {
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
        Accept: 'application/json'
      }
    }).then(function (response) {
      return response.text();
    }).catch(function (err) {
      console.warn('[SC Cart Queue] debounced attribute write failed', err);
      return '';
    });
  }

  function flushDebouncedNote() {
    noteDebounceTimer = null;
    if (pendingNote === null) return Promise.resolve();
    var note = attributeToString(pendingNote);
    pendingNote = null;

    return window.SC_cartFetch('/cart/update.js', {
      method: 'POST',
      body: JSON.stringify({ note: note }),
      credentials: 'same-origin',
      headers: {
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
        Accept: 'application/json'
      }
    }).then(function (response) {
      return response.text();
    }).catch(function (err) {
      console.warn('[SC Cart Queue] debounced note write failed', err);
      return '';
    });
  }

  var JSON_HEADERS = {
    'Content-Type': 'application/json',
    Accept: 'application/json',
    'X-Requested-With': 'XMLHttpRequest'
  };

  window.SC_executeGiftMutation = function (url, init) {
    var jobUrl = normalizeUrl(url);
    return enqueueMutation(url, init || { method: 'POST' }, 'system').then(function (response) {
      if (!response || !response.ok) return null;
      return resolveCartAfterMutation(response, jobUrl);
    });
  };

  window.SC_cartFetch = function (input, init) {
    var url = normalizeUrl(input);
    var method = requestMethod(input, init);

    if (method === 'POST' && isCartMutationUrl(url)) {
      var origin = (init && init.SC_ORIGIN) || 'user';
      return enqueueMutation(input, init || { method: 'POST' }, origin);
    }

    return nativeFetch(input, init);
  };

  window.fetch = function (input, init) {
    return window.SC_cartFetch(input, init);
  };

  window.SC_debouncedUpdateAttribute = function (key, value) {
    pendingAttributes[key] = value;
    clearTimeout(attrDebounceTimer);
    attrDebounceTimer = setTimeout(flushDebouncedAttributes, ATTR_DEBOUNCE_MS);
    return Promise.resolve();
  };

  window.SC_debouncedUpdateNote = function (note) {
    pendingNote = note;
    clearTimeout(noteDebounceTimer);
    noteDebounceTimer = setTimeout(flushDebouncedNote, ATTR_DEBOUNCE_MS);
    return Promise.resolve();
  };

  window.SC_isDebouncedAttrKey = function (key) {
    return DEBOUNCED_ATTR_KEYS.indexOf(key) !== -1;
  };

  var inflightSharedCart = null;
  var SHARED_CART_MAX_AGE_MS = 3000;

  window.SC_getSharedCart = function (opts) {
    opts = opts || {};
    if (!opts.forceFresh) {
      var cached = window.SC_getLastCart(SHARED_CART_MAX_AGE_MS);
      if (window.SC_isValidCart(cached)) return Promise.resolve(cached);
    }
    if (inflightSharedCart) return inflightSharedCart;
    inflightSharedCart = refreshCartFromServer()
      .catch(function () { return null; })
      .finally(function () {
        inflightSharedCart = null;
      });
    return inflightSharedCart;
  };

  window.SC_setLastCart = function (cart, at) {
    return cacheCartJson(cart, at);
  };

  window.SC_getLastCart = function (maxAgeMs) {
    maxAgeMs = maxAgeMs == null ? 2000 : maxAgeMs;
    if (!window.__SC_LAST_CART__ || !window.__SC_LAST_CART_AT__) return null;
    if (Date.now() - window.__SC_LAST_CART_AT__ > maxAgeMs) return null;
    return window.__SC_LAST_CART__;
  };

  window.SC_isValidCart = function (cart) {
    return !!(cart && typeof cart.item_count === 'number' && Array.isArray(cart.items));
  };

  window.SC_parseCartResponseText = function (text) {
    if (!text) return { cart: null, error: null };
    try {
      var data = JSON.parse(text);
      if (window.SC_isValidCart(data)) return { cart: data, error: null };
      if (data && data.status) return { cart: null, error: data };
      return { cart: null, error: null };
    } catch (e) {
      return { cart: null, error: null };
    }
  };

  window.SC_recoverCartAfterMutation = function () {
    var cartUrl = (window.theme && window.theme.routes && window.theme.routes.cart) || '/cart.js';
    var sep = cartUrl.indexOf('?') === -1 ? '?' : '&';
    return nativeFetch(cartUrl + sep + 't=' + Date.now(), {
      credentials: 'same-origin',
      method: 'GET'
    })
      .then(function (res) { return res.json(); })
      .then(cacheCartJson);
  };

  window.SC_changeItemSafe = function (key, qty) {
    if (!window.theme || !theme.cart || !theme.cart.changeItem) {
      return Promise.resolve(null);
    }
    return theme.cart.changeItem(key, qty).then(function (text) {
      var parsed = window.SC_parseCartResponseText(text);
      if (parsed.cart) return parsed.cart;
      return window.SC_recoverCartAfterMutation().catch(function () { return null; });
    }).catch(function () {
      return window.SC_recoverCartAfterMutation().catch(function () { return null; });
    });
  };

  window.SC_emitCartBuild = function () {
    clearTimeout(buildCoalesceTimer);
    buildCoalesceTimer = setTimeout(function () {
      document.dispatchEvent(new CustomEvent('cart:build'));
    }, CART_EVENT_COALESCE_MS);
  };

  window.SC_emitCartUpdated = function (cart) {
    if (cart && cart.items) cacheCartJson(cart);
    pendingUpdatedCart = cart || pendingUpdatedCart;
    clearTimeout(updatedCoalesceTimer);
    updatedCoalesceTimer = setTimeout(function () {
      var detailCart = pendingUpdatedCart;
      pendingUpdatedCart = null;
      if (!detailCart) return;
      document.dispatchEvent(
        new CustomEvent('cart:updated', {
          bubbles: true,
          detail: { cart: detailCart }
        })
      );
    }, CART_EVENT_COALESCE_MS);
  };

  window.SC_emitCartRefresh = function (cart) {
    if (cart && cart.items) cacheCartJson(cart);
    window.SC_emitCartUpdated(cart);
    window.SC_emitCartBuild();
  };
})();
